import { z } from 'zod/v3';
import {
  ActionResult,
  ApplicableContext,
  BaseAction,
  ExecuteContext,
  ActionTrackMetadata,
} from '@cotera/client/v0/actions/framework';
import type { ActionTrackingContext } from '@cotera/client/v0/actions/framework/action-tracking';
import { err, ok } from 'neverthrow';
import { CocoChatModel } from '../models/coco-chat.model';
import type { TenantedClient } from '@cotera/client/app/etc/api/typed-client';
import {
  CocoEvent,
  cocoActionMetadata,
  getChatSurface,
} from '@cotera/client/app/etc';

const inputSchema = z.object({
  pressed: z.boolean(),
});

export class CocoToggleAutoApproveAction extends BaseAction<
  z.infer<typeof inputSchema>,
  { t: string; message?: string }[]
> {
  title = 'Toggle Auto-Approve Tools';
  inputSchema = inputSchema;
  group = 'Chat';
  description = 'Enable or disable auto-approval of tool calls';

  constructor(private readonly client: TenantedClient['resource']['chat']) {
    super();
  }

  applicable(context: ApplicableContext): boolean {
    return context.hasTargetedModelOfType(CocoChatModel);
  }

  async execute(
    params: z.infer<typeof inputSchema>,
    context: ExecuteContext
  ): Promise<ActionResult<{ t: string; message?: string }[]>> {
    const model = context.targetedModelOfType(CocoChatModel);
    if (model === null) {
      return err([{ t: 'error', message: 'Coco chat model not found' }]);
    }

    model.autoApproveToolsWatchable.set(params.pressed);

    const result = await this.client.addAction({
      conversationId: model.conversationId,
      action: params.pressed
        ? { type: 'auto_approve' }
        : { type: 'remove_auto_approve' },
    });

    if (result.isErr()) {
      const isNotFound =
        result.error.t === 'application' &&
        result.error.err.errorType === 'NotFound';
      if (isNotFound) {
        // Conversation not created yet; local state is correct for next submit
        return ok({});
      }
      model.autoApproveToolsWatchable.set(!params.pressed);
      const errorMessage =
        result.error.t === 'application'
          ? result.error.err.message
          : 'Failed to toggle auto-approve';
      return err([{ t: 'error', message: errorMessage }]);
    }

    return ok({});
  }

  track({
    payload,
    context,
  }: ActionTrackingContext<
    z.infer<typeof inputSchema>,
    { t: string; message?: string }[]
  >): ActionTrackMetadata | null {
    const model = context.targetedModelOfType(CocoChatModel);
    if (model === null) {
      return null;
    }

    return cocoActionMetadata(CocoEvent.AutoApproveToggled, {
      conversationId: model.conversationId,
      surface: getChatSurface(context),
      pressed: payload.pressed,
    });
  }
}
